import { G } from '@mobily/ts-belt';
import { AxiosError, AxiosResponse, isAxiosError } from 'axios';

export const EXISTING_EMAIL_ERROR_CODE = 'existing_email' as const;
export const OTHER_ERROR_CODE = 'other_error' as const;

type RegistrationErrorMessage = {
  code: string;
  message?: string;
};

/**
 * This error response type is used for multiple endpoints
 * This error response follows the [Stitches format](https://github.com/stitchfix/stitches/wiki/Error-Handling)
 * and is documented across a few places:
 *
 * - https://unwritten.stitchfix.com/apidocs/web/client-auth-api/registration_(v1)_web/post_-_403_-_register_a_new_account_without_required_params.html
 * - https://unwritten.stitchfix.com/apidocs/web/client-auth-api/registration_(v1)_web/post_-_400_-_register_a_new_account_with_invalid_email_address.html
 * - https://unwritten.stitchfix.com/apidocs/web/client-auth-api/registration_(v1)_web/post_-_400_-_register_a_new_account_with_already_taken_email_address.html
 * - https://unwritten.stitchfix.com/apidocs/web/client-auth-api/registration_(v1)_web/post_-_400_-_register_a_new_account_with_an_invalid_business_line.html
 * - https://unwritten.stitchfix.com/apidocs/web/client-auth-api/validate_emails_(v1)/post_-_403_-_validate_email_no_parameters_sent.html
 * - https://unwritten.stitchfix.com/apidocs/web/client-auth-api/validate_emails_(v1)/post_-_403_-_validate_email_with_invalid_csrf_token.html
 */
export type ResponseError =
  | {
      errors: RegistrationErrorMessage[];
      email_error?: string;
      status: 400;
    }
  | {
      success?: false;
      error?: string;
      status: 403;
    };

type RegistrationEmailError = AxiosError<
  Extract<ResponseError, { status: 400 }>
>;

export const isExistingEmailError = (
  error: unknown,
): error is RegistrationEmailError => {
  if (!isAxiosError(error)) return false;

  const { response } = error;

  if (!response || response.status !== 400) return false;

  const data = response.data;

  if (!G.isObject(data) || !('errors' in data) || !G.isArray(data.errors)) {
    return false;
  }

  return data.errors.some(
    (e: RegistrationErrorMessage) =>
      G.isObject(e) && 'code' in e && e.code === EXISTING_EMAIL_ERROR_CODE,
  );
};

export type RequestPasswordResetResponse = {
  message: string;
};

interface RequestPasswordResetError extends AxiosError {
  response: AxiosResponse<RequestPasswordResetResponse>;
}

const isRequestPasswordResetResponseError = (
  response: AxiosResponse,
): response is RequestPasswordResetError['response'] => response.status === 422;

export const isRequestPasswordResetError = (
  error: unknown,
): error is RequestPasswordResetError =>
  isAxiosError(error) &&
  G.isObject(error.response) &&
  isRequestPasswordResetResponseError(error.response);

export type ChangePasswordResponse = {
  message: string;
  redirect_url: string;
};

type ChangePasswordErrorResponse = {
  message?: string;
  reset_password_token?: string;
  error?: { code: string };
};

interface ChangePasswordError extends AxiosError {
  response: AxiosResponse<ChangePasswordErrorResponse>;
}

const isChangePasswordResponseError = (
  response: AxiosResponse,
): response is ChangePasswordError['response'] => response.status === 422;

export const isChangePasswordError = (
  error: unknown,
): error is ChangePasswordError =>
  isAxiosError(error) &&
  G.isObject(error.response) &&
  isChangePasswordResponseError(error.response);
